import React, { useCallback } from 'react';
import PropTypes from 'prop-types';
import { Box } from '@mui/material';

// Import custom hooks
import useProjectTasks from '../../../hooks/useProjectTasks';
import useTaskMenu from '../../../hooks/useTaskMenu';
import useTaskMutations from '../../../hooks/useTaskMutations';
import useBuildTasks from '../../../hooks/useBuildTasks';
import useSection from '../../../hooks/useSection';
import useAuth from '../../../hooks/useAuth';

// Import components
import LoadingView from './LoadingView';
import ErrorView from './ErrorView';
import EmptyStateView from './EmptyStateView';
import PhaseHeader from './PhaseHeader';
import TasksGrid from './TasksGrid';
import TaskMenu from './TaskMenu';

/**
 * Build Component
 * Shows the development plan and build phases as a grid of tasks
 * 
 * @param {Object} props - Component props
 * @param {string} props.projectId - The project ID
 * @param {Object} props.project - The project data
 * @param {Function} props.showNotification - Function to show notifications
 * @param {Function} props.onOpenTaskSelector - Function to open task selector dialog
 * @returns {React.ReactElement} Build component
 */
const Build = ({ projectId, project: propProject, showNotification, onOpenTaskSelector }) => {
  // Get project data and operations
  const {
    project,
    setLocalProject,
    refreshProject,
    updateProject,
    toggleTaskStatus,
    isUpdatingProject,
    isTogglingTaskStatus
  } = useProjectTasks({ 
    projectId, 
    initialProject: propProject 
  });

  // Get sections and their tasks from backend
  const { 
    sections, 
    isLoadingSections, 
    sectionsError, 
    refetchSections 
  } = useSection({ activeOnly: true });
  
  // Get current user information
  const { user } = useAuth();

  // Process tasks and organize them into build phases
  const { 
    filteredTasks, 
    groupedPhases,
    isGeneratingPlan,
    generateBuildPlan
  } = useBuildTasks({ 
    project, 
    sections, 
    isLoadingSections, 
    user 
  });

  // Set up menu handling
  const {
    anchorEl,
    selectedTile,
    handleMenuOpen,
    handleMenuClose,
    handleTileClick,
    handleDownloadPDF,
    handleDownloadDOCX,
    handleEdit
  } = useTaskMenu({ 
    projectId, 
    project, 
    showNotification 
  });

  // Set up task mutations
  const { 
    markTaskAsDone, 
    removeTask 
  } = useTaskMutations({
    projectId,
    project,
    toggleTaskStatus,
    updateProject,
    getProject: refreshProject,
    setLocalProject,
    showNotification,
    refetchSections
  });

  // Handler to open the task selector dialog
  const handleOpenTaskSelector = useCallback(async (event) => {
    event.stopPropagation();
    event.preventDefault();
    
    // Before opening the task selector, fetch the latest project data
    if (projectId) {
      try {
        await refreshProject();
      } catch (error) {
        console.error('Error refreshing project before opening task selector:', error);
      }
    }
    
    // Call the provided function to open the task selector
    setTimeout(() => {
      if (onOpenTaskSelector) {
        onOpenTaskSelector();
      }
    }, 100); // Small delay to ensure state updates complete
  }, [onOpenTaskSelector, projectId, refreshProject]);

  // Generate a new build plan when requested
  const handleGeneratePlan = useCallback(async () => {
    if (!projectId) return;
    
    try {
      await generateBuildPlan(projectId);
      showNotification({
        message: 'Development plan generated successfully',
        type: 'success'
      });
    } catch (error) {
      console.error('Error generating build plan:', error);
      showNotification({
        message: `Failed to generate development plan: ${error.message || 'Unknown error'}`,
        type: 'error'
      });
    }
  }, [projectId, generateBuildPlan, showNotification]);

  return (
    <Box sx={{ width: '100%', pt: 4, px: { xs: 3, sm: 4, md: 5, lg: 6 } }}>
      {/* Loading state */}
      {(isLoadingSections || isGeneratingPlan) && <LoadingView />}
      
      {/* Error state */}
      {sectionsError && <ErrorView error={sectionsError} onRetry={refetchSections} />}
      
      {/* Empty state */}
      {!isLoadingSections && !isGeneratingPlan && !sectionsError && filteredTasks.length === 0 && (
        <EmptyStateView 
          onAddTasks={onOpenTaskSelector ? handleOpenTaskSelector : undefined} 
          onGeneratePlan={handleGeneratePlan}
        />
      )}
      
      {/* Tasks organized by build phases */}
      {!isLoadingSections && !isGeneratingPlan && !sectionsError && filteredTasks.length > 0 && (
        <Box sx={{ 
          width: '100%', 
          maxWidth: { xs: '1600px', xl: '1800px' }, 
          mx: 'auto'
        }}>
          {/* Organize tasks by phases */}
          {Object.entries(groupedPhases).map(([phaseName, phaseTasks], phaseIndex) => (
            <React.Fragment key={phaseName}>
              {/* Phase as a row with name on left and tasks on right */}
              <Box 
                sx={{ 
                  display: 'flex',
                  flexDirection: { xs: 'column', md: 'row' },
                  gap: { xs: 2, md: 4 },
                  mb: { xs: 2, md: 0 },
                  mt: 0
                }}
              >
                {/* Phase header */}
                <PhaseHeader 
                  phaseName={phaseName} 
                  taskCount={phaseTasks.length}
                  completedCount={phaseTasks.filter(task => task.status === 'completed').length}
                />
                
                {/* Tasks grid */}
                <TasksGrid 
                  phaseTasks={phaseTasks} 
                  onTileClick={handleTileClick} 
                  onMenuOpen={handleMenuOpen} 
                />
              </Box>
              
              {/* Add phase divider unless it's the last phase */}
              {phaseIndex !== Object.keys(groupedPhases).length - 1 && (
                <div className="phase-divider" />
              )}
            </React.Fragment>
          ))}
        </Box>
      )}
      
      {/* Menu for task actions */}
      <TaskMenu 
        anchorEl={anchorEl}
        selectedTile={selectedTile}
        onClose={handleMenuClose}
        onEdit={handleEdit}
        onDownloadPDF={handleDownloadPDF}
        onDownloadDOCX={handleDownloadDOCX}
        onMarkAsDone={markTaskAsDone}
        onRemove={removeTask}
        showNotification={showNotification}
      />
    </Box>
  );
};

Build.propTypes = {
  projectId: PropTypes.string.isRequired,
  project: PropTypes.object,
  showNotification: PropTypes.func.isRequired,
  onOpenTaskSelector: PropTypes.func
};

export default Build;